import { AssetSubClassCode } from '@api/api/entity-constants';
import { Entity } from '@api/api/entity-schema';
import { SnakeMeta } from '@api/api/timeseries-schema';
import { TabLines } from '@design/components/tab-lines/TabLines';
import { TabsType } from '@terminal/modules/tab-content/types';
import {
  isCountryOrArea,
  useCountryGdp,
} from '@terminal/react-query-hooks/country-gdp/useCountryGdp';
import {
  isLongTail,
  useLongTailSeries,
} from '@terminal/react-query-hooks/long-tail-series/useLongTailSeries';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import clsx from 'clsx';
import { EntityHeader } from '../entity-header/EntityHeader';
import { LegacyEntityHeader } from '../entity-header/LegacyEntityHeader';
import { ChartPrice } from '../simple-chart/components/chart-price/ChartPrice';
import { getTabs } from '../tab-content/utils';
import styles from './AssetOverviewHeader.module.scss';

export interface AssetOverviewHeaderProps {
  snakeMeta?: SnakeMeta;
  entity: Entity;
  activeTab: TabsType;
  changeTab: (index: number, tabKey: TabsType) => void;
  showShadow: boolean;
  documentsCount?: number;
  isDocumentsLoading?: boolean;
  isSeriesLoading?: boolean;
}

const AssetOverviewHeaderContent = ({
  snakeMeta,
  entity,
  activeTab,
  changeTab,
  showShadow,
  documentsCount = 0,
  isDocumentsLoading = false,
  isSeriesLoading = false,
}: AssetOverviewHeaderProps) => {
  const { isNewEarningsTabEnabled, isAoPulseEnabled } = useFlags(state => ({
    isNewEarningsTabEnabled: state.getFeatureFlagState(
      'PLATFORM-4671-ao-earnings'
    ),
    isAoPulseEnabled: state.getFeatureFlagState('PLATFORM-5199-ao-pulse'),
  }));

  const isStock = entity.sub_class === AssetSubClassCode.EquityStock;
  const isEcoCentralBank =
    entity.sub_class === AssetSubClassCode.EcoCentralBank;
  const isDocumentsTabVisible =
    (isStock || isEcoCentralBank) && (isDocumentsLoading || !!documentsCount);
  const tabs = getTabs({
    options: {
      isStock,
      isDocumentsTabVisible,
      isChartTabVisible:
        isSeriesLoading || isStock || !!entity.default_snake.trim(),
    },
    isNewEarningsTabEnabled,
  });
  const filteredTabs = tabs.filter(tab => !tab.isHidden);
  const visibleActiveTab = filteredTabs.some(tab => tab.tabKey === activeTab)
    ? activeTab
    : TabsType.Overview;

  return (
    <div
      className={clsx(styles.header, {
        [styles.showShadow]: showShadow,
      })}
    >
      <div className={styles.layout}>
        <div
          className={clsx(styles.headerContent, {
            [styles.noEntity]: !entity,
          })}
        >
          {entity && (
            <>
              <div className={styles.contentWrapper}>
                {isAoPulseEnabled ? (
                  <EntityHeader
                    entity={entity}
                    priceDisplayNode={
                      <ChartPrice
                        entity={entity}
                        snakeMeta={snakeMeta}
                        isSeriesLoading={isSeriesLoading}
                        enablePriceHover={false}
                      />
                    }
                  />
                ) : (
                  <LegacyEntityHeader
                    entity={entity}
                    priceDisplayNode={
                      <ChartPrice
                        entity={entity}
                        snakeMeta={snakeMeta}
                        isSeriesLoading={isSeriesLoading}
                        enablePriceHover={false}
                      />
                    }
                  />
                )}
              </div>
              <TabLines
                tabs={filteredTabs}
                activeTab={visibleActiveTab}
                onChange={changeTab}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const CountryAssetOverviewHeader = (props: AssetOverviewHeaderProps) => {
  const resolved = useCountryGdp(props.entity);
  return <AssetOverviewHeaderContent {...props} {...resolved} />;
};

const LongTailAssetOverviewHeader = (props: AssetOverviewHeaderProps) => {
  const resolved = useLongTailSeries(props.entity);
  return <AssetOverviewHeaderContent {...props} {...resolved} />;
};

export const AssetOverviewHeader = (props: AssetOverviewHeaderProps) =>
  isCountryOrArea(props.entity) ? (
    <CountryAssetOverviewHeader {...props} />
  ) : isLongTail(props.entity) ? (
    <LongTailAssetOverviewHeader {...props} />
  ) : (
    <AssetOverviewHeaderContent {...props} />
  );
